'use client'

import Link from 'next/link'
import { useState } from 'react'
import { HEADER_NAV_LINKS } from '~/data/navigation'
import CloseIcon from '../../../icons/close-icon'
import ArrowDown from './arrow-down'
import { useLocale } from '~/contexts/LocaleContext'
import { getTranslation } from '~/lib/get-translation'
import { useLocalizedNavigation } from '~/hooks/use-localized-navigation'

interface NavLink {
  href: string
  title: string
  children?: Array<{
    href: string
    title: string
  }>
}

interface SidebarMenuProps {
  isOpen: boolean
  onClose: () => void
}

interface NavItemProps {
  link: NavLink
  onClose: () => void
}

function NavItem({ link, onClose }: NavItemProps) {
  const [isOpen, setIsOpen] = useState(false)

  if (!link.children) {
    return (
      <Link href={link.href} className="block text-gray-800 hover:underline" onClick={onClose}>
        {link.title}
      </Link>
    )
  }

  return (
    <div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between px-4 py-3 text-gray-800 hover:underline"
      >
        <span>{link.title}</span>
        <div
          className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        >
          <ArrowDown className="text-gray-800" />
        </div>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-96' : 'max-h-0'
        }`}
      >
        {link.children?.map((child) => (
          <Link
            key={child.href}
            href={child.href}
            className="block px-6 py-2 text-gray-600 hover:underline"
            onClick={onClose}
          >
            {child.title}
          </Link>
        ))}
      </div>
    </div>
  )
}

export default function SidebarMenu({ isOpen, onClose }: SidebarMenuProps) {
  const { locale } = useLocale()
  const t = getTranslation(locale)
  const { getLocalizedHref } = useLocalizedNavigation()

  return (
    <div
      className={`fixed inset-0 z-50 bg-black/50 transition-opacity duration-300 ${
        isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
      }`}
      onClick={onClose}
    >
      <div
        className={`absolute left-0 top-0 h-screen w-[320px] bg-white p-10 transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex h-full flex-col gap-2">
          {/* Close button */}
          <div className="flex justify-end">
            <button onClick={onClose} aria-label="Close menu">
              <CloseIcon className="h-6 w-6 text-gray-800" />
            </button>
          </div>

          {/* Menu content */}
          <div className="flex flex-col gap-2 overflow-y-auto">
            {HEADER_NAV_LINKS.filter((r) => !r.showPCNavBar).map((link) => (
              <NavItem
                key={link.href}
                link={{
                  href: getLocalizedHref(link.href),
                  title: t(link.titleKey),
                  children: link.children?.map((child) => ({
                    href: getLocalizedHref(child.href),
                    title: t(child.titleKey),
                  })),
                }}
                onClose={onClose}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
